<script setup>

import { inject, ref, computed, watch } from 'vue';
import { 
	getTypeOccurrenceLien,
    getTypeOccurrenceLienReciproque,
    getTypeLien,
    TypeNommage } from '@/scripts/global.js';
import { 
    getErreurNomNorme,
    getErreurTexteRequis, 
    getErreurValeurListeRequise } from '@/scripts/controlesSaisie.js';
import { 
	apiAjouterChamp,
    apiModifierInfosChamp } from '@/services/ServiceModele.js';
import Dialog from 'primevue/dialog';
import MultiSelect from 'primevue/multiselect';
import Formulaire from '@/components/generiques/Formulaire.vue';
import SaisieTexte from '@/components/generiques/SaisieTexte.vue';
import SaisieTypeDonnee from '@/components/generiques/SaisieTypeDonnee.vue';
import SaisieTypeOccurrence from '@/components/generiques/SaisieTypeOccurrence.vue';
import SaisieModeEvaluation from '@/components/generiques/SaisieModeEvaluation.vue';
import ModaleAjoutDimension from '@/components/modales/ModaleAjoutDimension.vue';

const emit = defineEmits(['fermeture', 'ajoutChamp']);

const { champEdite } = defineProps({
    champEdite: Object
});

const getTypeDonnee = inject('getTypeDonnee');
const getTypeObjet = inject('getTypeObjet');
const getChamp = inject('getChamp');
const dimensions = inject('dimensions');
const getDimension = inject('getDimension');
const ouvrirFenetreFormule = inject('ouvrirFenetreFormule');
const appelerApiModifModele = inject('appelerApiModifModele');

const champ = ref(getChampInitial());
const affichageModaleAjoutDimension = ref(false);

const estNouveauChamp = computed(() => {
    return (champ.value.id > 0 ? false : true);
});

const typeObjet = computed(() => {
    return getTypeObjet(champEdite.idTypeObjet);
});

const estChampLien = computed(() => {
    return champ.value.typeDonnee?.classe === 'OBJET';
});

const typeObjetLie = computed(() => {
    if (estChampLien.value) {
        return getTypeObjet(champ.value.typeDonnee?.id);
    }
});

const iconeTypeLien = computed(() => {
    if (!champ.value.typeLien) {
        return 'fa-question';
    }
    switch(champ.value.typeLien) {
        case 'APPARTIENT_A':
            return 'fa-share-alt rotation';
        case 'CONTIENT':
            return 'fa-share-alt';
        case 'ASSOCIE_A':
            return 'fa-arrows-left-right';
    }
});

watch(() => champ.value.typeOccurrence, (typeOccurrence) => {
    if (typeOccurrence === 'PLUSIEURS') {
        champ.value.typeOccurrenceReciproque = 'UNE';
    } else if (typeOccurrence === 'UNE' && !champ.value.typeOccurrenceReciproque) {
        champ.value.typeOccurrenceReciproque = 'PLUSIEURS';
    }
    champ.value.typeLien = getTypeLien(champ.value.typeOccurrence, champ.value.typeOccurrenceReciproque);
});

watch(() => champ.value.typeOccurrenceReciproque, (typeOccurrenceReciproque) => {
    if (typeOccurrenceReciproque === 'PLUSIEURS') {
        champ.value.typeOccurrence = 'UNE';
    } else if (typeOccurrenceReciproque === 'UNE' && !champ.value.typeOccurrence) {
        champ.value.typeOccurrence = 'PLUSIEURS';
    }
    champ.value.typeLien = getTypeLien(champ.value.typeOccurrence, champ.value.typeOccurrenceReciproque);
});

function getChampInitial() {
    return {
        id: champEdite.id,
        idTypeObjet: champEdite.idTypeObjet,
        nom: champEdite.nom,
        typeDonnee: getTypeDonnee(champEdite.typeDonnee),
        modeEvaluation: champEdite.modeEvaluation,
        typeLien: champEdite.typeLien,
        typeOccurrence: getTypeOccurrenceLien(champEdite.typeLien),
        nomChampReciproque: getNomChampReciproque(champEdite),
        typeOccurrenceReciproque: getTypeOccurrenceLienReciproque(champEdite.typeLien),
        dimensions: champEdite.dimensions ? champEdite.dimensions.map(idDimension => getDimension(idDimension)) : []
    };
}

function getNomChampReciproque(champ) {
    if (champ.nomChampReciproque) {
        return champ.nomChampReciproque;
    } else if (champ.typeDonnee?.classe === 'OBJET' && champ.idChampReciproque) {
        return getChamp(champ.typeDonnee.id, champ.idChampReciproque).nom;
    } else {
        return null;
    }
}

function afficherModaleAjoutDimension() {
    affichageModaleAjoutDimension.value = true;
}

function gererAjoutDimension(nomDimension) {
    champ.value.dimensions.push(dimensions.value.find(dim => dim.nom === nomDimension));
}

function gererFermeture(visible) {
    if (!visible) {
        emit('fermeture');
    }
}

function validerSaisie(champ) {
    const erreurs = {};
    erreurs.nom = getErreurNomNorme(champ.nom, TypeNommage.VARIABLE);
    erreurs.typeDonnee = getErreurValeurListeRequise(champ.typeDonnee);
    if (!erreurs.typeDonnee) {
        if (champ.modeEvaluation === 'CLE' && champ.typeDonnee.classe === 'OBJET') {
            erreurs.typeDonnee = 'Un champ clé ne peut pas avoir pour type un autre type d\'objet';
        } else if (champ.modeEvaluation === 'CLE_AUTO' && champ.typeDonnee.libelle !== 'Entier long') {
            erreurs.typeDonnee = 'Un champ clé automatique doit être de type Entier long';
        }
    }
    erreurs.modeEvaluation = getErreurValeurListeRequise(champ.modeEvaluation);
    erreurs.typeOccurrence = getErreurValeurListeRequise(champ.typeOccurrence, estChampLien.value);
    erreurs.nomChampReciproque = getErreurTexteRequis(champ.nomChampReciproque, estChampLien.value);
    erreurs.typeOccurrenceReciproque = getErreurValeurListeRequise(champ.typeOccurrenceReciproque, estChampLien.value);
    return erreurs;
}

function validerFormulaire() {
    champ.value.validationFormulaire = true;
}

function valider() {
    const champDTO = {
        idTypeObjet: champ.value.idTypeObjet,
        id: champ.value.id ? champ.value.id : null,
        nom: champ.value.nom,
        typeDonnee: champ.value.typeDonnee,
        modeEvaluation: champ.value.modeEvaluation,
        dimensions: champ.value.dimensions.map(dim => dim.id),
        typeLien: champ.value.typeLien,
        nomChampReciproque: champ.value.nomChampReciproque
    };
    console.log(champDTO);
    const api = (estNouveauChamp.value ? apiAjouterChamp : apiModifierInfosChamp);
    appelerApiModifModele(api, champDTO, (reponseOK) => {
        if (reponseOK) {
            if (estNouveauChamp.value) {
                const nouveauChamp = typeObjet.value.champs.find(chp => chp.nom === champ.value.nom);
                emit('ajoutChamp', nouveauChamp);
                if (nouveauChamp.modeEvaluation === 'CALCUL') {
                    ouvrirFenetreFormule(nouveauChamp, true);
                }
            }
            emit('fermeture');
        }
    });
}

</script>

<template>
    <Dialog 
        :visible="true" 
        modal 
        @update:visible="gererFermeture">
            <template #header>
                <label class="titre">{{ estNouveauChamp ? `Création d'un champ` : `Modification d'un champ` }}</label>
            </template>
            <Formulaire
                :style="{gridTemplateColumns: `repeat(${estChampLien ? 4 : 2}, auto)`}"
                :objet="champ"
                :fonction-validation-saisie="validerSaisie"
                @validation="valider">
                <label class="intitule">Type d'objet :</label>
                <label>{{ typeObjet.nom }}</label>
                <template v-if="estChampLien">
                    <div class="col-separateur">
                        <i class="fa-solid fa-link icone-lien"
                            v-tooltip="'Lien entre deux types d\'objet'"/>
                    </div>
                    <label>{{ typeObjetLie.nom }}</label>
                </template>
                <label class="intitule">Nom du champ :</label>
                <SaisieTexte 
                    v-model:valeur="champ.nom" 
                    :id-erreur="'nom'" 
                    :autofocus="true" />
                <template v-if="estChampLien">
                    <div class="col-separateur" />
                    <SaisieTexte 
                        v-model:valeur="champ.nomChampReciproque" 
                        :id-erreur="'nomChampReciproque'" />
                </template>
                <label class="intitule">Type :</label>
                <SaisieTypeDonnee 
                    v-model:valeur="champ.typeDonnee" 
                    :id-erreur="'typeDonnee'"
                    :proposer-types-objets="typeObjet.typeOccurrence === 'PLUSIEURS'" 
                    :desactive="false" />
                <template v-if="estChampLien">
                    <div class="col-separateur" />
                    <SaisieTypeDonnee 
                        :valeur="getTypeDonnee({classe: 'OBJET', id: typeObjet.id})" 
                        :id-erreur="'aucun'"
                        :proposer-types-objets="true" 
                        :desactive="true" />
                </template>
                <label class="intitule">Mode :</label>
                <SaisieModeEvaluation 
                    v-model:valeur="champ.modeEvaluation" 
                    :id-erreur="'modeEvaluation'"
                    :proposer-mode-cle="false" />
                <template v-if="estChampLien">
                    <div class="col-separateur" />
                    <SaisieModeEvaluation 
                        :valeur="champ.modeEvaluation" 
                        :id-erreur="'modeEvaluation'"
                        :proposer-mode-cle="true"
                        :desactive="true" />
                </template>
                <template v-if="estChampLien">
                    <label class="intitule">Nb. d'objets liés :</label>
                    <SaisieTypeOccurrence
                        v-model:valeur="champ.typeOccurrence" 
                        :id-erreur="'typeOccurrence'" />
                    <div class="col-separateur">
                        <i class="fa-solid icone-type-lien"
                            :class="iconeTypeLien"/>
                    </div>
                    <SaisieTypeOccurrence
                        v-model:valeur="champ.typeOccurrenceReciproque" 
                        :id-erreur="'typeOccurrenceReciproque'" />
                </template>
                <label class="intitule">Dimensions :</label>
                <div class="zone-dimensions">
                    <MultiSelect 
                        class="multiselect-dimensions"
                        v-model="champ.dimensions" 
                        :options="dimensions"
                        option-label="nom"
                        display="chip"
                        :showToggleAll="false" />
                    <button 
                        class="bouton fas fa-plus" 
                        v-tooltip="'Ajouter une dimension au modèle'"
                        @click="afficherModaleAjoutDimension()"/>
                </div>
                <template v-if="estChampLien">
                    <div class="col-separateur" />
                <MultiSelect 
                    class="multiselect-dimensions"
                    v-model="champ.dimensions" 
                    :options="dimensions"
                    option-label="nom"
                    display="chip"
                    :showToggleAll="false"
                    :disabled="true" />                
                </template>
            </Formulaire>
            <template #footer>
                <button 
                    class="bouton fas fa-check" 
                    :disabled="false"
                    @click="validerFormulaire()"/>
            </template>
    </Dialog>
    <ModaleAjoutDimension 
			v-if="affichageModaleAjoutDimension"
			:dimension="{}"  
            @fermeture="affichageModaleAjoutDimension = false"
			@ajoutDimension="gererAjoutDimension" />
</template>

<style scoped>

.col-separateur {
    width: 50px;
    display: grid;
    align-self: center;
    place-items: center;
}

.icone-lien {
	color: var(--couleur-champ-lien);
}

.icone-type-lien {
	color: var(--couleur-primaire);
}

.rotation {
    transform: rotate(180deg);
}

.zone-dimensions {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 2px;
}

.multiselect-dimensions {
    flex-grow: 1;
}


</style>